import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Globe, ChevronDown, User, Shield, BadgeCheck } from 'lucide-react';
import { Button } from './ui/button';
import { useState } from 'react';
import { useLanguage, languages } from '../contexts/LanguageContext';
import { useEducationAuth } from '../contexts/EducationAuthContext';
import { getTranslation } from '../translations';
import { getProfessionalIdentity } from '../config/professionalIdentity';
import socialLinks, { SocialIcon } from '../config/socialLinks';
import AboutMenu from './AboutMenu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from './ui/dropdown-menu';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, changeLanguage } = useLanguage();
  const { user, isAuthenticated, isAdmin, logout } = useEducationAuth();
  const t = (key) => getTranslation(language, key);
  
  // Get bilingual professional identity
  const PROFESSIONAL_IDENTITY = getProfessionalIdentity(language);

  const currentLanguage = languages.find((lang) => lang.code === language);

  const isAdminRoute = location.pathname.includes('/admin');

  if (isAdminRoute) {
    return null;
  }

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="mx-auto w-full max-w-[1920px] px-4">
        <div className="flex items-center justify-between gap-4 min-h-20 py-2">
          {/* Logo */}
          <div
            className="flex items-center gap-3 sm:gap-4 cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
            onClick={() => navigate('/')}
            data-testid="header-logo"
          >
            <img
              src={PROFESSIONAL_IDENTITY.logoUrl}
              alt={PROFESSIONAL_IDENTITY.organization}
              className="h-10 sm:h-12 w-auto object-contain flex-shrink-0"
            />
            <div className="flex flex-col min-w-0 ml-1">
              <h1 className="text-base sm:text-lg lg:text-xl xl:text-lg 2xl:text-xl font-bold text-gray-900 whitespace-nowrap">{PROFESSIONAL_IDENTITY.organization}</h1>
              <p className="text-xs text-gray-600 hidden sm:block">{language === 'es' ? 'Centro Educativo' : 'Education Center'}</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex flex-nowrap items-center justify-end gap-x-0.5 gap-y-1 min-w-0 [&>button]:h-9 [&>button]:px-2.5 [&>button]:text-[13px] [&>button]:whitespace-nowrap" data-testid="header-desktop-nav">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-gray-700 hover:text-blue-600 hover:bg-blue-50"
              data-testid="nav-home"
            >
              {t('nav.home')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/topics')}
              className="text-gray-700 hover:text-blue-600 hover:bg-blue-50"
              data-testid="nav-topics"
            >
              {t('nav.topics')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/academy/sleep-reset')}
              className="text-gray-700 hover:text-amber-600 hover:bg-amber-50"
              data-testid="nav-academy-learn"
            >
              {t('nav.academy')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/tech-pocket-guide')}
              className="hidden min-[1880px]:inline-flex text-gray-700 hover:text-purple-600 hover:bg-purple-50 font-medium"
              data-testid="nav-tech-pocket-guide"
            >
              {t('nav.techPocketGuide')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/videos')}
              className="text-gray-700 hover:text-blue-600 hover:bg-blue-50"
            >
              {t('nav.videos')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/about')}
              className="hidden min-[1880px]:inline-flex text-gray-700 hover:text-blue-600 hover:bg-blue-50"
            >
              {t('nav.about')}
            </Button>
            <AboutMenu triggerClassName="px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md" />
            <Button
              variant="ghost"
              onClick={() => navigate('/organizations')}
              className="hidden min-[1880px]:inline-flex text-gray-700 hover:text-slate-800 hover:bg-slate-100"
            >
              {t('nav.organizations')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/coach')}
              className="hidden min-[1880px]:inline-flex text-gray-700 hover:text-emerald-600 hover:bg-emerald-50"
              data-testid="nav-sleep-coach"
            >
              {t('nav.sleepCoach') || 'Sleep Coach'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/sleep-assessment')}
              className="text-gray-700 hover:text-indigo-600 hover:bg-indigo-50"
              data-testid="nav-sleep-assessment"
            >
              {t('nav.sleepAssessment') || 'Sleep Assessment'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/wearable-insights')}
              className="hidden min-[1880px]:inline-flex text-gray-700 hover:text-violet-600 hover:bg-violet-50"
              data-testid="nav-wearable-insights"
            >
              {t('nav.wearableInsights') || 'Wearable Insights'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/membership')}
              className="text-gray-700 hover:text-blue-600 hover:bg-blue-50"
            >
              {t('nav.membership')}
            </Button>
            {/* Below 1880px the less-used links collapse into "More" so the bar stays on one row. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="min-[1880px]:hidden text-gray-700 hover:text-blue-600 hover:bg-blue-50" data-testid="nav-more-trigger">
                  {language === 'es' ? 'Más' : 'More'} <ChevronDown className="h-4 w-4 ml-0.5" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" data-testid="nav-more-menu">
                <DropdownMenuItem onClick={() => navigate('/tech-pocket-guide')} data-testid="nav-more-tech-pocket-guide">{t('nav.techPocketGuide')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/about')} data-testid="nav-more-about">{t('nav.about')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/organizations')} data-testid="nav-more-organizations">{t('nav.organizations')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/coach')} data-testid="nav-more-sleep-coach">{t('nav.sleepCoach') || 'Sleep Coach'}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/wearable-insights')} data-testid="nav-more-wearable-insights">{t('nav.wearableInsights') || 'Wearable Insights'}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Admin Button - Only visible for admin users */}
            {isAdmin && (
              <Button
                variant="ghost"
                onClick={() => navigate('/admin/hub')}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="header-admin-btn"
              >
                <Shield className="h-4 w-4 mr-1" />
                {t('nav.admin')}
              </Button>
            )}

            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="flex items-center gap-2 border-gray-300 hover:bg-gray-100"
                  data-testid="language-selector"
                >
                  <Globe className="h-4 w-4" />
                  <span className="font-semibold">
                    {currentLanguage?.flag} {currentLanguage?.code.toUpperCase()}
                  </span>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`cursor-pointer ${language === lang.code ? 'bg-blue-50 font-semibold' : ''}`}
                    data-testid={`language-option-${lang.code}`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Auth Section */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 border-gray-300 hover:bg-gray-100"
                    data-testid="header-user-menu"
                  >
                    <User className="h-4 w-4" />
                    <span className="max-w-24 truncate">{user?.first_name || t('common.account')}</span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem
                    onClick={() => navigate('/membership')}
                    className="cursor-pointer"
                  >
                    {t('common.myAccount')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/my-plan')}
                    className="cursor-pointer"
                    data-testid="header-user-menu-my-plan"
                  >
                    {language === 'es' ? 'Mi Plan' : 'My Plan'}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/academy/certificates')}
                    className="cursor-pointer"
                  >
                    {t('common.myCertificates')}
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => navigate('/admin/hub')}
                        className="cursor-pointer text-red-600"
                      >
                        <Shield className="h-4 w-4 mr-2" />
                        {t('nav.adminPanel')}
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => { logout(); navigate('/'); }}
                    data-testid="header-user-menu-sign-out"
                    className="cursor-pointer text-red-600"
                  >
                    {t('common.signOut')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-gray-400 font-normal">{t('common.followUs')}</DropdownMenuLabel>
                  <div className="flex items-center gap-2 px-2 py-1.5">
                    {socialLinks.map((social) => (
                      <a
                        key={social.name}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                        aria-label={social.name}
                        data-testid={`header-social-${social.icon}`}
                      >
                        <SocialIcon platform={social.icon} className="h-4 w-4" />
                      </a>
                    ))}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                onClick={() => navigate('/education/login')}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="header-login-btn"
              >
                {t('common.signIn')}
              </Button>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="xl:hidden p-2 rounded-lg hover:bg-gray-100"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label={isMobileMenuOpen
              ? (language === 'es' ? 'Cerrar menú de navegación' : 'Close navigation menu')
              : (language === 'es' ? 'Abrir menú de navegación' : 'Open navigation menu')}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-nav"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="xl:hidden py-4 border-t">
            <nav className="flex flex-col gap-2">
              <Button
                variant="ghost"
                onClick={() => { navigate('/'); setIsMobileMenuOpen(false); }}
                className="justify-start text-gray-700 hover:text-blue-600 hover:bg-blue-50"
              >
                {t('nav.home')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => { navigate('/topics'); setIsMobileMenuOpen(false); }}
                className="justify-start text-gray-700 hover:text-blue-600 hover:bg-blue-50"
                data-testid="nav-mobile-topics"
              >
                {t('nav.topics')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => { navigate('/academy/sleep-reset'); setIsMobileMenuOpen(false); }}
                className="justify-start text-gray-700 hover:text-amber-600 hover:bg-amber-50"
                data-testid="nav-mobile-academy-learn"
              >
                {t('nav.academy')}
              </Button>
              {/* ITER182 · Public discovery in mobile drawer */}
              <Button
                variant="ghost"
                onClick={() => { navigate('/cpap-fit'); setIsMobileMenuOpen(false); }}
                className="justify-start text-gray-700 hover:text-blue-600 hover:bg-blue-50"
                data-testid="nav-mobile-cpap-mask-finder"
              >
                {language === 'es' ? 'Encuentra tu máscara CPAP' : 'Find your CPAP mask'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => { navigate('/cpap-stories'); setIsMobileMenuOpen(false); }}
                className="justify-start text-gray-700 hover:text-emerald-600 hover:bg-emerald-50"
                data-testid="nav-mobile-cpap-stories"
              >
                {language === 'es' ? 'Historias de éxito CPAP' : 'CPAP Success Stories'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => { navigate('/tech-pocket-guide'); setIsMobileMenuOpen(false); }}
                className="justify-start text-gray-700 hover:text-purple-600 hover:bg-purple-50"
                data-testid="mobile-nav-tech-pocket-guide"
              >
                {t('nav.techPocketGuide')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => { navigate('/videos'); setIsMobileMenuOpen(false); }}
                className="justify-start text-gray-700 hover:text-blue-600 hover:bg-blue-50"
              >
                {t('nav.videos')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => { navigate('/about'); setIsMobileMenuOpen(false); }}
                className="justify-start text-gray-700 hover:text-blue-600 hover:bg-blue-50"
              >
                {t('nav.about')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => { navigate('/organizations'); setIsMobileMenuOpen(false); }}
                className="justify-start text-gray-700 hover:text-slate-800 hover:bg-slate-100"
                data-testid="mobile-nav-organizations"
              >
                {t('nav.organizations')}
              </Button>

              <Button
                variant="ghost"
                onClick={() => { navigate('/coach'); setIsMobileMenuOpen(false); }}
                className="justify-start text-gray-700 hover:text-emerald-600 hover:bg-emerald-50"
                data-testid="mobile-nav-sleep-coach"
              >
                {t('nav.sleepCoach') || 'Sleep Coach'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => { navigate('/sleep-assessment'); setIsMobileMenuOpen(false); }}
                className="justify-start text-gray-700 hover:text-indigo-600 hover:bg-indigo-50"
                data-testid="mobile-nav-sleep-assessment"
              >
                {t('nav.sleepAssessment') || 'Sleep Assessment'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => { navigate('/wearable-insights'); setIsMobileMenuOpen(false); }}
                className="justify-start text-gray-700 hover:text-violet-600 hover:bg-violet-50"
                data-testid="mobile-nav-wearable-insights"
              >
                {t('nav.wearableInsights') || 'Wearable Insights'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => { navigate('/membership'); setIsMobileMenuOpen(false); }}
                className="justify-start text-gray-700 hover:text-blue-600 hover:bg-blue-50"
              >
                {t('nav.membership')}
              </Button>

              {/* Auth Nav Item (Founder UI Request 2026-03-01) — placed after nav sections,
                  styled as a nav-item with subtle separator so it does not compete with
                  primary navigation. Signed-out: Sign In → /education/login (existing route).
                  Signed-in: My Account → /education/dashboard, shows first name if available. */}
              <div className="border-t border-gray-200 mt-2 pt-2">
                {isAuthenticated ? (
                  <Button
                    variant="ghost"
                    onClick={() => { navigate('/education/dashboard'); setIsMobileMenuOpen(false); }}
                    className="justify-start w-full text-gray-700 hover:text-blue-600 hover:bg-blue-50"
                    data-testid="mobile-nav-my-account"
                  >
                    <User className="mr-2 h-4 w-4" />
                    {language === 'es' ? 'Mi cuenta' : 'My Account'}
                    {user?.first_name ? <span className="ml-2 text-xs text-gray-500">· {user.first_name}</span> : null}
                  </Button>
                ) : null}
                {isAuthenticated ? (
                  <Button
                    variant="ghost"
                    onClick={() => { navigate('/my-plan'); setIsMobileMenuOpen(false); }}
                    className="justify-start w-full text-gray-700 hover:text-blue-600 hover:bg-blue-50"
                    data-testid="mobile-nav-my-plan"
                  >
                    <BadgeCheck className="mr-2 h-4 w-4" />
                    {language === 'es' ? 'Mi Plan' : 'My Plan'}
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    onClick={() => { navigate('/education/login'); setIsMobileMenuOpen(false); }}
                    className="justify-start w-full text-gray-700 hover:text-blue-600 hover:bg-blue-50"
                    data-testid="mobile-nav-sign-in"
                  >
                    <User className="mr-2 h-4 w-4" />
                    {language === 'es' ? 'Iniciar sesión' : 'Sign In'}
                  </Button>
                )}
              </div>

              {/* Language Switcher Mobile */}
              <div className="flex items-center gap-2 px-4 py-2">
                <Globe className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600">{t('common.language')}:</span>
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    data-testid={`mobile-language-option-${lang.code}`}
                    className={`px-2 py-1 rounded ${
                      language === lang.code ? 'bg-blue-100 text-blue-700' : 'text-gray-600'
                    }`}
                  >
                    {lang.flag}
                  </button>
                ))}
              </div>

              {/* Admin Button Mobile */}
              {isAdmin && (
                <Button
                  onClick={() => { navigate('/admin/hub'); setIsMobileMenuOpen(false); }}
                  variant="outline"
                  className="text-red-600 border-red-600 hover:bg-red-50 mt-2"
                >
                  <Shield className="mr-2 h-4 w-4" />
                  {t('nav.adminPanel')}
                </Button>
              )}

              {/* Auth Button Mobile — signed-in Sign Out only. Sign In lives in the
                  primary nav list above per Founder request. */}
              {isAuthenticated && (
                <Button
                  onClick={() => { logout(); navigate('/'); setIsMobileMenuOpen(false); }}
                  variant="outline"
                  className="text-red-600 border-red-600 hover:bg-red-50 mt-2"
                  data-testid="mobile-nav-sign-out"
                >
                  {t('common.signOut')}
                </Button>
              )}

              {/* Social Links Mobile */}
              <div className="flex items-center justify-center gap-4 pt-4 mt-4 border-t border-gray-200">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-500 hover:text-blue-600 transition-colors"
                    aria-label={social.name}
                    data-testid={`mobile-social-${social.icon}`}
                  >
                    <SocialIcon platform={social.icon} className="h-5 w-5" />
                  </a>
                ))}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
